import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    input,
    inject,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import {
    resolveColorContext,
    type ContextFamily,
    type ContextTone,
} from '@quipex/shared/utils';
import { QpxButton } from '../../directives/button/button';
import { QpxIcon } from '../../directives/icon/icon';
import {
    toDocumentExtensionContext,
    toDocumentExtensionLabel,
} from '../document-file-presentation/document-file-presentation';
import { toFileTypeSvg } from './file-type-icon-svg';

@Component({
    selector: 'qpx-file-type-icon',
    imports: [NgIcon, QpxIcon],
    templateUrl: './file-type-icon.html',
    styleUrl: './file-type-icon.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'img',
        '[attr.aria-label]': 'accessibleLabel()',
        '[class.boxed]': 'boxed()',
        '[class.mat-icon]': 'isButtonIcon',
        '[style.background-color]': 'boxed() ? resolvedSurface() : null',
    },
})
export class QpxFileTypeIcon {
    //#region Dependency injection
    protected readonly isButtonIcon =
        inject<ElementRef<HTMLElement>>(ElementRef).nativeElement.hasAttribute(
            'matButtonIcon'
        );
    private readonly button = inject(QpxButton, {
        optional: true,
        skipSelf: true,
    });
    //#endregion

    //#region Inputs
    public readonly extension = input.required<string>();
    public readonly appearance = input<'icon-only' | 'boxed'>('icon-only');
    public readonly decorative = input<boolean | undefined, unknown>(
        undefined,
        {
            transform: (value) =>
                value === undefined ? undefined : booleanAttribute(value),
        }
    );
    public readonly size = input<'xs' | 'sm' | 'md' | 'lg'>();
    public readonly context = input<ContextFamily>();
    public readonly tone = input<ContextTone>('default');
    public readonly strokeWidth = input<string | number>();
    public readonly color = input<string>();
    //#endregion

    //#region State
    protected readonly boxed = computed(() => this.appearance() === 'boxed');
    protected readonly isDecorative = computed(
        () => this.decorative() ?? this.boxed()
    );
    protected readonly resolvedSize = computed(
        () => this.size() ?? this.button?.size() ?? 'md'
    );
    protected readonly accessibleLabel = computed(() =>
        this.extension()
            ? `File extension ${toDocumentExtensionLabel(this.extension())}`
            : 'File'
    );
    protected readonly resolvedContext = computed(
        () =>
            this.context() ??
            (this.color() || (!this.boxed() && !this.isDecorative())
                ? undefined
                : toDocumentExtensionContext(this.extension()))
    );
    protected readonly resolvedSurface = computed(
        () =>
            `var(${resolveColorContext(this.resolvedContext() ?? 'secondary', this.tone()).surface.variable})`
    );
    protected readonly resolvedColor = computed(() => {
        const context = this.resolvedContext();
        if (!context) return this.color();
        const colors = resolveColorContext(context, this.tone());
        return `var(${(this.isDecorative() ? colors.decorative : colors.foreground).variable})`;
    });
    protected readonly svg = computed(() => toFileTypeSvg(this.extension()));
    //#endregion
}
